import { formatDays } from "./trial-policy";

/**
 * Rendering a trial-policy audit entry legibly, rather than as raw JSON.
 *
 * `AuditLogs.tsx` renders every entry's `changes` column as `JSON.stringify` inside a `<pre>`.
 * That is right for eleven action types nobody has designed a view for, and wrong for this
 * one: the requirement is that the log shows who changed the trial length, from what, to what
 * and why — and `{"setting":"trialDurationDays","previousValue":14,...}` satisfies the letter
 * of that and not the point.
 */

/** The payload the server writes for a trial-policy change. One shape, fixed by contract. */
export interface TrialPolicyChanges {
  readonly setting: "trialDurationDays";
  readonly previousValue: number;
  readonly newValue: number;
  readonly reason: string;
}

/**
 * The payload if it is a trial-policy change, else `null`.
 *
 * A predicate rather than a cast. `changes` is a `text` column holding whatever was
 * `JSON.stringify`d into it, and this file reads rows written by a version of the server that
 * may not be this one — so every field is checked, and a row that does not match falls back to
 * the existing `<pre>` rather than rendering a half-built sentence.
 *
 * **Takes a string OR an already-parsed object**, because the same payload arrives as both: the
 * database column is text, and the admin client's own response type declares
 * `changes: Record<string, unknown> | null` because the API layer has already parsed it. A
 * function that took only the string would have been correct about the column and wrong about
 * every call site in this package.
 */
export function parseTrialPolicyChanges(
  raw: string | Record<string, unknown> | null,
): TrialPolicyChanges | null {
  if (raw === null || raw === "") return null;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Malformed JSON is not an error worth surfacing: the row still renders through the
      // `<pre>` fallback, which is what it did before this function existed.
      return null;
    }
  }
  if (parsed === null || typeof parsed !== "object") return null;
  const candidate = parsed as Record<string, unknown>;
  if (candidate.setting !== "trialDurationDays") return null;
  if (typeof candidate.previousValue !== "number" || typeof candidate.newValue !== "number")
    return null;
  if (typeof candidate.reason !== "string") return null;
  return {
    setting: "trialDurationDays",
    previousValue: candidate.previousValue,
    newValue: candidate.newValue,
    reason: candidate.reason,
  };
}

/**
 * The collapsed row's delta, in the four pieces M13 draws it in.
 *
 * **M13 renders two of them to a screen reader only.** The approved fragment is
 *
 *     <span class="m13-sr">Changed from </span>14 days
 *     <span class="m13-arrow" aria-hidden="true">→</span>
 *     <span class="m13-sr"> to </span>21 days
 *
 * where `.m13-sr` is the standard clip-rect hide. So the operator READS `14 days → 21 days` and a
 * screen reader HEARS *"Changed from 14 days to 21 days"* — the arrow carrying the relation for
 * one and the words carrying it for the other, neither said twice.
 *
 * This used to return one flat string containing both, which put the screen-reader words on screen
 * and produced the visibly wrong *"Changed from 14 days → to 21 days"*. The stray *"→ to"* is the
 * tell: it only parses if one of the two was never meant to be seen.
 *
 * **The unit word is not decoration.** This sits on a page listing eleven other action types, where
 * `14 → 21` does not say what unit it is in — and this run carries an amendment whose entire
 * subject is that a duration without its correctly-inflected unit reads wrong. `formatDays`
 * supplies the singular at 1, which an operator reaches with one keystroke.
 */
export function trialPolicyDelta(changes: TrialPolicyChanges): {
  readonly fromLabel: string;
  readonly from: string;
  readonly toLabel: string;
  readonly to: string;
} {
  return {
    fromLabel: "Changed from ",
    from: formatDays(changes.previousValue),
    toLabel: " to ",
    to: formatDays(changes.newValue),
  };
}

/** The expanded rows, in the approved order and with the approved labels. */
export function trialPolicyRows(
  changes: TrialPolicyChanges,
): readonly { readonly label: string; readonly value: string; readonly spanTwo: boolean }[] {
  return [
    // `Trial duration: 14 days`, not a bare numeral — the same reason as the summary above.
    {
      label: "From",
      value: `Trial duration: ${formatDays(changes.previousValue)}`,
      spanTwo: false,
    },
    { label: "To", value: `Trial duration: ${formatDays(changes.newValue)}`, spanTwo: false },
    // `Why` is the approved label, not `Reason`. And it spans BOTH columns: the page pairs
    // Admin/Target and IP Address/User Agent in a two-column grid, so three single cells shift
    // everything after them by one and orphan User Agent. `spanTwo` is what keeps the page's
    // standing pairs intact for this one action type.
    { label: "Why", value: changes.reason, spanTwo: true },
  ];
}
